import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Erzeugungsanlage } from '../../../domain/model/erzeugungsanlage';
import { WestnetzSidenavService } from '../../../service/common/westnetz-sidenav/westnetz-sidenav.service';
import { ConfirmDialogService } from '../../../service/common/dialog/confirm-dialog.service';
import { NotificationService } from '../../../service/common/notification/notification.service';
import { RoutingService } from '../../../service/common/routing/routing.service';
import { ErzeugungsanlageService } from '../../../service/erzeugungsanlage/erzeugungsanlage.service';
import { ErzeugungsanlagenRefreshService } from '../../../service/erzeugungsanlage/erzeugungsanlagen-refresh.service';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { debounceTime, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { Anchor } from '../../../domain/utils/anchor';
import { NavigationEnd, Router } from '@angular/router';
import { Energietraeger, EnergietraegerItem } from '../../../domain/model/energietraeger';
import { BlindleistungsverfahrenItem } from '../../../domain/model/blindleistungsverfahren.enum';
import { Leistung } from '../../../domain/model/leistung';
import { AbstractForm } from '../abstract-form.model';
import { ControlConfig, FormGroupConfig } from '../form-config.model';
import { NetzzuordnungSubFormComponent } from '../netzzuordnung-sub-form/netzzuordnung-sub-form.component';
import { AnlagenAnschriftSubFormComponent } from '../anlagen-anschrift-sub-form/anlagen-anschrift-sub-form.component';
import { AbregelungSubFormComponent } from '../abregelung-sub-form/abregelung-sub-form.component';
import { GeokoordinatenSubFormComponent } from '../geokoordinaten-sub-form/geokoordinaten-sub-form.component';
import {
    SteuerbareResourceSubFormComponent,
} from '../steuerbare-resource-sub-form/steuerbare-resource-sub-form.component';
import {
    TechnischeResourceSubFormComponent,
} from '../technische-resource-sub-form/technische-resource-sub-form.component';
import { MarktlokationSubFormComponent } from '../marktlokation-sub-form/marktlokation-sub-form.component';
import { BetriebSubFormComponent } from '../betrieb-sub-form/betrieb-sub-form.component';
import { AbrechnungSubFormComponent } from '../abrechnung-sub-form/abrechnung-sub-form.component';
import { SolarSubFormComponent } from '../solar-sub-form/solar-sub-form.component';
import { WindSubFormComponent } from '../wind-sub-form/wind-sub-form.component';
import { BatteriespeicherSubFormComponent } from '../batteriespeicher-sub-form/batteriespeicher-sub-form.component';
import { AnlagenschluesselValidator } from '../validator/anlagenschluessel.validator';
import { MatValidators } from '../../../common/material/components/mat-form-field-required.directive';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { Abrechnungsmodell } from '../../../domain/model/abrechnungsmodell.model';
import { Abrechnungsmodelltyp, AbrechnungsmodelltypItem } from '../../../domain/model/abrechnungsmodell-typ.enum';
import { SolarAbsenkung70 } from '../../../domain/model/solar-absenkung-70.enum';
import {
    FunkrundsteuerEmpfaengerInformationen,
} from '../../../domain/model/funkrundsteuer-empfeanger-informationen.model';
import {
    FunkrundsteuerEmpfaengerAbregelmoeglichkeit,
} from '../../../domain/model/funkrundsteuer-empfaenger-abregelmoeglichkeit.model';
import { SolarInformationen } from '../../../domain/model/solar-informationen.model';
import { LeistungsValidator } from '../validator/leistungs.validator';
import { TnbUser } from '../../../service/auth/tnb-user';
import { FeldBeschreibungService } from '../../../service/feldbeschreibung/feldbeschreibung.service';
import { Feld } from '../../../domain/model/feld.enum';
import { FeldBeschreibung } from '../../../domain/model/feldbeschreibung.model';
import { EMPTY, EmptyError, firstValueFrom, iif, lastValueFrom, Observable, Subject, switchMap } from 'rxjs';
import { Verguetungsart, VerguetungsartItem } from '../../../domain/model/verguetungsart.enum';
import { ResourceIdsSubFormComponent } from '../resource-ids-sub-form/resource-ids-sub-form.component';
import { TrancheSubFormComponent } from '../tranche-sub-form/tranche-sub-form.component';
import { PredicateValidator } from '../validator/predicate.validator';
import { TeilgebietSelectionService } from '../../../service/common/teilgebiet/teilgebiet-selection.service';
import moment, { Moment } from 'moment';
import { Netzbetreiber } from '../../../domain/model/netzbetreiber';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

@Component({
    selector: 'redispatch-erzeugungsanlage-detail-form',
    templateUrl: './erzeugungsanlage-detail-form.component.html',
    styleUrls: [
        './erzeugungsanlage-detail-form.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ErzeugungsanlageDetailFormComponent extends AbstractForm<Erzeugungsanlage> implements OnInit, OnDestroy, AfterViewInit {
    public static readonly SHARED_BRUTTONENNLEISTUNG_VALIDATOR = [
        ...LeistungsValidator.validator3,
    ];

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly VNB_INTERN_MAX_LENGTH = 40;
    public readonly TODAY_EOB: Moment = moment().endOf('day');

    public errorCount = 0;

    public readonly ANCHORS = [
        new Anchor<Feld>('anchor-basisdaten', Feld.HEADER_BASISDATA),
        new Anchor<Feld>('anchor-netzzuordnung', Feld.HEADER_NETZZUORDNUNG),
        new Anchor<Feld>('anchor-lokation', Feld.HEADER_LOKATION),
        new Anchor<Feld>('anchor-leistung', Feld.HEADER_LEISTUNG),
        new Anchor<Feld>('anchor-steuerung', Feld.HEADER_STEUERUNG),
        new Anchor<Feld>('anchor-rd-sr-ids', Feld.HEADER_RESOURCE_IDS),
        new Anchor<Feld>('anchor-rd-sr-objekt', Feld.HEADER_REDISPATCH_SR),
        new Anchor<Feld>('anchor-rd-tr-objekt', Feld.HEADER_REDISPATCH_TR, [
            new Anchor<Feld>('anchor-marktlokation', Feld.HEADER_TR_MARKTLOKATION),
            new Anchor<Feld>('anchor-tranche', Feld.HEADER_TR_TRANCHE),
            new Anchor<Feld>('anchor-betrieb', Feld.HEADER_TR_BETRIEB),
            new Anchor<Feld>('anchor-tr-leistung', Feld.HEADER_TR_LEISTUNG),
            new Anchor<Feld>('anchor-abrechnung', Feld.HEADER_TR_ABRECHNUNG),
            new Anchor<Feld>('anchor-photovoltaik', Feld.HEADER_TR_SOLAR),
            new Anchor<Feld>('anchor-wind', Feld.HEADER_TR_WIND),
            new Anchor<Feld>('anchor-batteriespeicher', Feld.HEADER_TR_SPEICHER),
        ]),
    ];

    @ViewChild(NetzzuordnungSubFormComponent, { static: true }) netzzuordnungForm: NetzzuordnungSubFormComponent;
    @ViewChild(AnlagenAnschriftSubFormComponent, { static: true }) anschriftForm: AnlagenAnschriftSubFormComponent;
    @ViewChild(GeokoordinatenSubFormComponent, { static: true }) geokoordinatenForm: GeokoordinatenSubFormComponent;
    @ViewChild(AbregelungSubFormComponent, { static: true }) abregelungForm: AbregelungSubFormComponent;
    @ViewChild(ResourceIdsSubFormComponent, { static: true }) resourceIdsForm: ResourceIdsSubFormComponent;
    @ViewChild(SteuerbareResourceSubFormComponent, { static: true }) steuerbareResourceForm: SteuerbareResourceSubFormComponent;
    @ViewChild(TechnischeResourceSubFormComponent, { static: true }) technischeResourceForm: TechnischeResourceSubFormComponent;
    @ViewChild(MarktlokationSubFormComponent, { static: true }) marktlokationForm: MarktlokationSubFormComponent;
    @ViewChild(TrancheSubFormComponent, { static: true }) trancheForm: TrancheSubFormComponent;
    @ViewChild(BetriebSubFormComponent, { static: true }) betriebForm: BetriebSubFormComponent;
    @ViewChild(AbrechnungSubFormComponent, { static: true }) abrechnungForm: AbrechnungSubFormComponent;
    @ViewChild(SolarSubFormComponent, { static: true }) solarForm: SolarSubFormComponent;
    @ViewChild(WindSubFormComponent, { static: true }) windForm: WindSubFormComponent;
    @ViewChild(BatteriespeicherSubFormComponent, { static: true }) batterieForm: BatteriespeicherSubFormComponent;

    @Input({ required: true })
    public createNewMode = false;

    @Input({ required: true })
    public readonly = false;

    @Input({ required: true })
    public netzbetreiber!: Netzbetreiber;

    @Input()
    public erzeugungsanlage?: Erzeugungsanlage;

    public readonly VerguetungsartItems = VerguetungsartItem.getAll();
    public readonly EnergietraegerItems = EnergietraegerItem.getAll();
    public readonly BlindleistungsverfahrenItems = BlindleistungsverfahrenItem.getAll();

    public feldBeschreibungen: Map<string, FeldBeschreibung> = new Map();
    public readonly Feld = Feld;

    public stadtwerkeId!: string;

    public profile: TnbUser;
    public funkrundsteuerEmpfaengerInformationen$: Observable<FunkrundsteuerEmpfaengerInformationen | null>;

    protected initialized = false;
    protected redispatchRelevantFlagAutoFixed = false;

    private saveOrCreateButtonClicked = false;
    private onDestroy$ = new Subject<void>();

    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlageRefreshService = inject(ErzeugungsanlagenRefreshService);
    private confirmDialogService = inject(ConfirmDialogService);
    private notificationService = inject(NotificationService);
    private routingService = inject(RoutingService);
    private router = inject(Router);
    private westnetzSidenavService = inject(WestnetzSidenavService);
    private anlagenschluesselValidator = inject(AnlagenschluesselValidator);
    private feldBeschreibungService = inject(FeldBeschreibungService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        super(inject(UntypedFormBuilder));

        this.router.events
            .pipe(takeUntilDestroyed())
            .subscribe(s => {
                if (s instanceof NavigationEnd) {
                    const tree = this.router.parseUrl(this.router.url);
                    if (tree.fragment) {
                        const element = document.querySelector('#' + tree.fragment);
                        if (element) {
                            setTimeout(() => {
                                element.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'nearest',
                                });
                            }, 100);
                        }
                    }
                }
            });
    }

    get formGroupConfig(): FormGroupConfig<Erzeugungsanlage> {
        return {
            gueltigAb: [
                null,
                MatValidators.required,
            ],
            verguetungsart: [
                null,
                MatValidators.required,
            ],
            anlagenschluessel: [
                '',
                {
                    validators: Validators.maxLength(AnlagenschluesselValidator.MAX_LENGTH),
                    asyncValidators: this.anlagenschluesselValidator.validate(this.erzeugungsanlage?.dokumentenId),
                    updateOn: 'change',
                },
            ],
            energietraeger: [
                null,
                MatValidators.required,
            ],
            bezVnbIntern: [
                null,
                Validators.maxLength(this.VNB_INTERN_MAX_LENGTH),
            ],
            netzzuordnung: this.netzzuordnungForm.initForm(),
            anlagenAnschrift: this.anschriftForm.initForm(),
            geokoordinaten: this.geokoordinatenForm.initForm(),
            leistung: this.fb.group(this.leistungFormGroupConfig),
            funkrundsteuerEmpfaengerInformationen: this.abregelungForm.initForm(),
            resourceIds: this.resourceIdsForm.initForm(),
            redispatchRelevant: [
                {
                    value: true,
                    disabled: true,
                },
            ],
            steuerbareResourceInformationen: this.steuerbareResourceForm.initForm(),
            technischeResourceInformationen: this.technischeResourceForm.initForm(),
            marktlokation: this.marktlokationForm.initForm(),
            tranche: this.trancheForm.initForm(),
            betriebsdaten: this.betriebForm.initForm(),
            abrechnungsmodell: this.abrechnungForm.initForm(),
            solarInformationen: this.solarForm.initForm(),
            windInformationen: this.windForm.initForm(),
            batterieSpeicherInformationen: this.batterieForm.initForm(),
        } as unknown as FormGroupConfig<Erzeugungsanlage>; // Needed to ignore missing/backend values like id and dokumentenId
    }

    get leistungFormGroupConfig(): FormGroupConfig<Leistung> {
        return {
            bruttonennleistung: [
                null,
                [
                    MatValidators.required,
                    ...ErzeugungsanlageDetailFormComponent.SHARED_BRUTTONENNLEISTUNG_VALIDATOR,
                ],
            ],
            blindleistungsverfahren: [
                null,
                MatValidators.required,
            ],
            nettonennleistungProduktion: [
                { value: null, disabled: true },
                [
                    ...LeistungsValidator.validator3,
                ],
            ],
            nettonennleistungVerbrauch: [
                { value: null, disabled: true },
                [
                    ...LeistungsValidator.validator3,
                    PredicateValidator.conditionalValidator(() => this.isBatterie, Validators.required),
                ],
            ],
            nettoengpassleistungProduktion: [
                { value: null, disabled: true },
                [
                    ...LeistungsValidator.validator3,
                ],
            ],
            nettoengpassleistungVerbrauch: [
                { value: null, disabled: true },
                [
                    ...LeistungsValidator.validator3,
                    PredicateValidator.conditionalValidator(() => this.isBatterie, Validators.required),
                ],
            ],
        };
    }

    public get fLeistung(): ControlConfig<Leistung> {
        return (this.f.leistung as UntypedFormGroup).controls as ControlConfig<Leistung>;
    }

    public get fAbregelung(): ControlConfig<FunkrundsteuerEmpfaengerInformationen> {
        return (this.f.funkrundsteuerEmpfaengerInformationen as UntypedFormGroup).controls as ControlConfig<FunkrundsteuerEmpfaengerInformationen>;
    }

    public get abregelmoeglichkeit(): FunkrundsteuerEmpfaengerAbregelmoeglichkeit | null {
        return this.fAbregelung.abregelmoeglichkeit.value;
    }

    public get netzzuordnungId(): string | undefined {
        return this.netzzuordnungForm?.selectedNetzzuordnung?.netzzuordnungId;
    }

    public get isSteuerbareAbregelungRequired(): boolean {
        return this.netzbetreiber.redispatchEnabled && this.fLeistung.bruttonennleistung.value >= 100;
    }

    public get isNotRedispatchRelevant(): boolean {
        return !this.f?.redispatchRelevant?.value;
    }

    public get fAbrechnung(): ControlConfig<Abrechnungsmodell> {
        return (this.f.abrechnungsmodell as UntypedFormGroup).controls as ControlConfig<Abrechnungsmodell>;
    }

    public get fSolar(): ControlConfig<SolarInformationen> {
        return (this.f.solarInformationen as UntypedFormGroup).controls as ControlConfig<SolarInformationen>;
    }

    public get isSolar(): boolean {
        return this.f.energietraeger.value === Energietraeger.SOLAR;
    }

    public get isWind(): boolean {
        const value = this.f.energietraeger.value;
        return value === Energietraeger.WINDENERGIE_OFFSHORE || value === Energietraeger.WINDENERGIE_ONSHORE;
    }

    public get isBatterie(): boolean {
        return this.f.energietraeger.value === Energietraeger.BATTERIESPEICHER;
    }

    public get isAnlagenkennlinieRequired(): boolean {
        const abrechnungsmodell = this.fAbrechnung.abrechnungsmodell?.value;
        return this.isWind && abrechnungsmodell != null && abrechnungsmodell !== Abrechnungsmodelltyp.PAUSCHAL;
    }

    public get isSpitzOrSpitzLight(): boolean {
        return this.fAbrechnung.abrechnungsmodell?.value === Abrechnungsmodelltyp.SPITZ ||
            this.fAbrechnung.abrechnungsmodell?.value === Abrechnungsmodelltyp.SPITZLIGHT;
    }

    public get spitzOrSpitzLightHint(): string {
        const val = AbrechnungsmodelltypItem.getAll()
            .filter(item => item.key == Abrechnungsmodelltyp.SPITZ || item.key == Abrechnungsmodelltyp.SPITZLIGHT)
            .map(item => item.description);
        return `Pflichtfeld wenn unter Abrechnungsmodell ${val} ausgewählt wurde`;
    }

    public get currentPath(): string {
        return this.router.url.startsWith(this.routingService.erzeugungsanlagePath()) ?
            this.routingService.erzeugungsanlagePath() : this.routingService.netzzuordnungPath;
    }

    protected get showDeleteButton(): boolean {
        return !this.createNewMode &&
            this.stadtwerkeId === this.erzeugungsanlage?.netzzuordnung?.netzbetreiber?.stadtwerkeId;
    }

    async ngOnInit() {
        this.feldBeschreibungService.invalidateCache();
        this.feldBeschreibungService.listFeldBeschreibungAsMap()
            .pipe(take(1))
            .subscribe(feldBeschreibungen => {
                this.feldBeschreibungen = feldBeschreibungen;
                this.changeDetectorRef.markForCheck();
            });

        this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();

        this.initForm();
        this.subscribeOnFormChanges();
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    ngAfterViewInit() {
        if (this.readonly) {
            this.form.disable();
        }

        if (!this.createNewMode) {
            // späte Initialisierung, da die Subformen erst nach dem ngOnInit initialisiert werden
            // und die Subformen die Daten der Erzeugungsanlage benötigen
            // Das netzzuordnungInitialized$ wird "später" nach Initialisierung aller ViewChild
            // gefeuert, und verzögert somit
            // => für late init: im AfterViewChecked per Counter Guard nur 1x aufrufen
            this.netzzuordnungForm.netzzuordnungInitialized$
                .pipe(
                    take(1),
                    tap(() => {
                        this.changeDetectorRef.detach();
                        const { redispatchRelevant, ...erzeugungsanlage } = this.erzeugungsanlage;
                        this.form.patchValue(erzeugungsanlage);
                        if (redispatchRelevant !== this.f.redispatchRelevant.value) {
                            this.redispatchRelevantFlagAutoFixed = true;
                        }
                        this.geokoordinatenForm.formChanged();
                        this.updateAbrechnungsForm(this.erzeugungsanlage);
                        this.updateAbregelmoeglichkeit(this.erzeugungsanlage);
                    }),
                    mergeMap(() => fromPromise(this.updateNetzzuordnungsForm(this.erzeugungsanlage))),
                )
                .subscribe(() => {
                    this.changeDetectorRef.reattach();
                    this.changeDetectorRef.detectChanges();

                    this.initialized = true;
                    this.markAllAsTouched();

                    this.countInvalidControls();
                    if (this.errorCount > 0) {
                        this.notificationService.success('Invalide Felder rot markiert.');
                    }
                });
        } else {
            this.initialized = true;
        }

        this.form.statusChanges
            .pipe(
                debounceTime(0),
                takeUntil(this.onDestroy$),
            )
            .subscribe(() => {
                this.countInvalidControls();
            });
    }

    public sanitize(erzeugeranlage: Erzeugungsanlage): Erzeugungsanlage {
        erzeugeranlage.redispatchRelevant = this.f.redispatchRelevant.value;
        erzeugeranlage.netzzuordnung = this.netzzuordnungForm.sanitize(erzeugeranlage.netzzuordnung);
        erzeugeranlage.anlagenAnschrift = this.anschriftForm.sanitize(erzeugeranlage.anlagenAnschrift);
        erzeugeranlage.geokoordinaten = this.geokoordinatenForm.sanitize(erzeugeranlage.geokoordinaten);
        erzeugeranlage.leistung = this.sanitizeLeistung(erzeugeranlage.leistung, erzeugeranlage.redispatchRelevant);
        erzeugeranlage.funkrundsteuerEmpfaengerInformationen = this.abregelungForm.sanitize(erzeugeranlage.funkrundsteuerEmpfaengerInformationen);

        if (erzeugeranlage.redispatchRelevant) {
            erzeugeranlage.steuerbareResourceInformationen = this.steuerbareResourceForm.sanitize(erzeugeranlage.steuerbareResourceInformationen);
            erzeugeranlage.technischeResourceInformationen = this.technischeResourceForm.sanitize(erzeugeranlage.technischeResourceInformationen);
            erzeugeranlage.marktlokation = this.marktlokationForm.sanitize(erzeugeranlage.marktlokation);
            erzeugeranlage.tranche = this.trancheForm.sanitize(erzeugeranlage.tranche);
            erzeugeranlage.betriebsdaten = this.betriebForm.sanitize(erzeugeranlage.betriebsdaten);
            erzeugeranlage.abrechnungsmodell = this.abrechnungForm.sanitize(erzeugeranlage.abrechnungsmodell);
            if (this.isSolar) {
                erzeugeranlage.solarInformationen = this.solarForm.sanitize(erzeugeranlage.solarInformationen);
            } else {
                erzeugeranlage.solarInformationen = null;
            }
            if (this.isWind) {
                erzeugeranlage.windInformationen = this.windForm.sanitize(erzeugeranlage.windInformationen);
            } else {
                erzeugeranlage.windInformationen = null;
            }
            if (this.isBatterie) {
                erzeugeranlage.batterieSpeicherInformationen = this.batterieForm.sanitize(erzeugeranlage.batterieSpeicherInformationen);
            } else {
                erzeugeranlage.batterieSpeicherInformationen = null;
            }
        } else {
            erzeugeranlage.steuerbareResourceInformationen = null;
            erzeugeranlage.technischeResourceInformationen = null;
            erzeugeranlage.marktlokation = null;
            erzeugeranlage.tranche = null;
            erzeugeranlage.betriebsdaten = null;
            erzeugeranlage.abrechnungsmodell = null;
            erzeugeranlage.solarInformationen = null;
            erzeugeranlage.windInformationen = null;
            erzeugeranlage.batterieSpeicherInformationen = null;
        }
        return erzeugeranlage;
    }

    public sanitizeLeistung(leistung: Leistung, isRedispatchRelevant: boolean): Leistung {
        if (!isRedispatchRelevant) {
            leistung.nettoengpassleistungProduktion = null;
            leistung.nettoengpassleistungVerbrauch = null;
            leistung.nettonennleistungProduktion = null;
            leistung.nettonennleistungVerbrauch = null;
        }
        return leistung;
    }

    public async resetForm() {
        this.erzeugungsanlage = await firstValueFrom(
            this.erzeugungsanlageService.getErzeugungsanlage(this.erzeugungsanlage.dokumentenId),
        );
        this.changeDetectorRef.detach();

        this.form.reset();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { redispatchRelevant, ...erzeugungsanlage } = this.erzeugungsanlage;
        this.form.patchValue(erzeugungsanlage);
        this.geokoordinatenForm.formChanged();
        this.updateAbregelmoeglichkeit(this.erzeugungsanlage);
        await this.updateNetzzuordnungsForm(this.erzeugungsanlage);
        this.markAllAsTouched();

        this.changeDetectorRef.reattach();
        this.changeDetectorRef.detectChanges();
    }

    public isSaveButtonDisabled(): boolean {
        return !this.form.valid ||
            this.saveOrCreateButtonClicked ||
            this.erzeugungsanlage.netzzuordnung == null;
    }

    public isCreateButtonDisabled(): boolean {
        return !this.form.valid ||
            this.saveOrCreateButtonClicked ||
            this.f.netzzuordnung.value == null;
    }

    public async deleteErzeugungsanlage() {
        if (await this.confirmDialogService.confirm(
            'Möchten Sie diese Anlage wirklich löschen?',
            'Anlage löschen',
            'warn',
            'Löschen')) {
            await lastValueFrom(this.erzeugungsanlageService.deleteErzeugungsanlage(this.erzeugungsanlage.dokumentenId));

            await this.notificationService.success('Ausgewählte Erzeugungsanlage wurde gelöscht.');
            this.erzeugungsanlageRefreshService.requestRefresh();
            this.closeSideNav();
        }
    }

    public closeSideNav() {
        this.westnetzSidenavService.closeRightnav();
        this.router.navigate([
            this.routingService.erzeugungsanlagePath(),
        ], {
            queryParams: { erzeugungsanlage: null, createnew: null },
            queryParamsHandling: 'merge',
        });
    }

    public navigateToAnchor(anchorId: string): void {
        this.router.navigate([
            this.currentPath,
        ], {
            fragment: anchorId,
            queryParamsHandling: 'merge',
        });
    }

    protected getFeldBeschreibung(feld: Feld): FeldBeschreibung | null {
        return this.feldBeschreibungen[feld];
    }

    protected markAllAsTouched() {
        this.form.markAllAsTouched();
    }

    protected getAnchor(...ids: number[]): Anchor<Feld> {
        let last = ids.pop();
        let anchors = ids.reduce((a, currentValue) => a[currentValue].sublevel, this.ANCHORS);
        return anchors[last];
    }

    protected async saveOrCreateErzeugungsanlage() {
        try {
            this.saveOrCreateButtonClicked = true;
            if (this.createNewMode) {
                await lastValueFrom(this.erzeugungsanlageService.createErzeugungsanlage(
                    this.stadtwerkeId,
                    this.sanitize(this.form.value),
                ));
                this.notificationService.success('Neue Erzeugungsanlage wurde angelegt.');
            } else {
                let sanitizedErzeugungsanlage = this.sanitize(this.form.value);
                sanitizedErzeugungsanlage.dokumentenId = this.erzeugungsanlage?.dokumentenId;
                sanitizedErzeugungsanlage.id = this.erzeugungsanlage?.id;
                await lastValueFrom(this.saveErzeugungsanlage(
                    this.stadtwerkeId,
                    sanitizedErzeugungsanlage,
                ));
                this.notificationService.success('Ausgewählte Erzeugungsanlage wurde geändert.');
            }
            this.erzeugungsanlageRefreshService.requestRefresh();
            this.closeSideNav();
        } catch (e) {
            if (e instanceof EmptyError) {
                // It's 'fine'. saveErzeugungsanlage was called with confirm dialog and then canceled
            } else {
                await this.confirmDialogService.confirm(
                    'Das hat leider nicht geklappt. Möglicherweise ist die Internetverbindung unterbrochen oder der Server hat ein Problem. ' +
                    'Versuchen Sie bitte, den ' +
                    'Vorgang nochmal zu wiederholen. Sollte es dann immer noch nicht funktionieren, kontaktieren Sie bitte ' +
                    'Ihren Kundenbetreuer.',
                    'Server Fehler',
                    'warn',
                    'OK',
                    null,
                );
            }
        } finally {
            this.saveOrCreateButtonClicked = false;
        }
    }

    private subscribeOnFormChanges(): void {
        this.fAbregelung.abregelmoeglichkeit
            .valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(abregelmoeglichkeit => {
                if (FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FIX70 === abregelmoeglichkeit) {
                    this.fSolar.absenkung70.setValue(SolarAbsenkung70.YES);
                } else {
                    this.fSolar.absenkung70.setValue(SolarAbsenkung70.NO);
                }

                if (this.netzbetreiber.redispatchEnabled) {
                    if (FunkrundsteuerEmpfaengerAbregelmoeglichkeit.KEINE === abregelmoeglichkeit ||
                        FunkrundsteuerEmpfaengerAbregelmoeglichkeit.FIX70 === abregelmoeglichkeit) {
                        this.f.redispatchRelevant.setValue(false);
                    } else {
                        this.f.redispatchRelevant.setValue(true);
                    }
                } else {
                    this.f.redispatchRelevant.setValue(false);
                }
            });

        this.f.verguetungsart
            .valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(value => {
                this.f.anlagenschluessel.clearValidators();
                this.f.anlagenschluessel.clearAsyncValidators();

                if (value === Verguetungsart.EEG) {
                    this.f.anlagenschluessel.addValidators(MatValidators.required);
                    this.f.anlagenschluessel.addAsyncValidators(
                        this.anlagenschluesselValidator.validate(this.erzeugungsanlage?.dokumentenId),
                    );
                } else {
                    this.f.anlagenschluessel.addValidators(Validators.maxLength(AnlagenschluesselValidator.MAX_LENGTH));
                }

                this.f.anlagenschluessel.updateValueAndValidity();
            });

        this.funkrundsteuerEmpfaengerInformationen$ =
            this.f.funkrundsteuerEmpfaengerInformationen
                .valueChanges
                .pipe(
                    debounceTime(0),
                    map((value) => {
                        if (this.f.funkrundsteuerEmpfaengerInformationen.valid) {
                            return this.abregelungForm.sanitize(value);
                        } else {
                            return null;
                        }
                    }),
                );
    }

    private updateAbrechnungsForm(ea: Erzeugungsanlage) {
        this.abrechnungForm.f.abrechnungsmodell.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.abrechnungForm.f.datenquelleSpitzLightModell.setValue(ea.abrechnungsmodell?.datenquelleSpitzLightModell);
                this.abrechnungForm.f.ausgewaehlterWetterdienstleister.setValue(ea.abrechnungsmodell?.ausgewaehlterWetterdienstleister);
                this.abrechnungForm.f.zugeordneteReferenzanlage.setValue(ea.abrechnungsmodell?.zugeordneteReferenzanlage);
                this.abrechnungForm.f.datenquelleSpitzLightModell.updateValueAndValidity();
                this.abrechnungForm.f.ausgewaehlterWetterdienstleister.updateValueAndValidity();
                this.abrechnungForm.f.zugeordneteReferenzanlage.updateValueAndValidity();
            });
        this.abrechnungForm.f.datenquelleSpitzLightModell.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.abrechnungForm.f.ausgewaehlterWetterdienstleister.setValue(ea.abrechnungsmodell?.ausgewaehlterWetterdienstleister);
                this.abrechnungForm.f.zugeordneteReferenzanlage.setValue(ea.abrechnungsmodell?.zugeordneteReferenzanlage);
                this.abrechnungForm.f.ausgewaehlterWetterdienstleister.updateValueAndValidity();
                this.abrechnungForm.f.zugeordneteReferenzanlage.updateValueAndValidity();
            });
    }

    private async updateNetzzuordnungsForm(ea: Erzeugungsanlage) {
        if (ea.netzzuordnung.ortsname !== null &&
            this.checkForNetzzuordnungsValue(ea.netzzuordnung.ortsname, this.netzzuordnungForm.ortsnamenSelection)) {
            await this.netzzuordnungForm.handleOrtsnameChanged(ea.netzzuordnung.ortsname);
            if (ea.netzzuordnung.stationsname !== null &&
                this.checkForNetzzuordnungsValue(ea.netzzuordnung.stationsname, this.netzzuordnungForm.stationsNamenSelection)) {
                await this.netzzuordnungForm.handleStationsnameChanged(ea.netzzuordnung.stationsname);
                if (ea.netzzuordnung.trafoFeldName !== null &&
                    this.checkForNetzzuordnungsValue(ea.netzzuordnung.trafoFeldName, this.netzzuordnungForm.trafoFeldNamenSelection)) {
                    await this.netzzuordnungForm.handleTrafoFeldNameChanged(ea.netzzuordnung.trafoFeldName);
                }
            }
        }
    }

    private checkForNetzzuordnungsValue(value: string, fullString: string[]): boolean {
        return fullString.includes(value);
    }

    private updateAbregelmoeglichkeit(erzeugungsanlage: Erzeugungsanlage) {
        if (erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelmoeglichkeit === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.IEC101_104) {
            this.abregelungForm.iecForm.abregelungstyp =
                this.abregelungForm.iecForm.determineTypeSelection(
                    erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenIEC101104.adressen);
            this.abregelungForm.iecForm.f.adressen.patchValue(
                erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenIEC101104.adressen);
        }
        if (erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelmoeglichkeit === FunkrundsteuerEmpfaengerAbregelmoeglichkeit.TASE2) {
            this.abregelungForm.tase2Form.abregelungstyp =
                this.abregelungForm.tase2Form.determineTypeSelection(
                    erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenTase2.adressen,
                );
            this.abregelungForm.tase2Form.f.adressen.patchValue(
                erzeugungsanlage.funkrundsteuerEmpfaengerInformationen.abregelungsinformationenTase2.adressen,
            );
        }
    }

    private saveErzeugungsanlage(stwId: string, erzeugungsanlage: Erzeugungsanlage): Observable<Erzeugungsanlage | never> {
        let checkErzeugungsanlageForPlanwertChanges$: Observable<boolean> = this.erzeugungsanlageService.checkErzeugungsanlageForPlanwertChanges(
            stwId,
            erzeugungsanlage,
        );
        let saveErzeugungsanlage$: Observable<Erzeugungsanlage> = this.erzeugungsanlageService.saveErzeugungsanlage(
            stwId,
            erzeugungsanlage,
        );
        let openConfirmPlanwertChangesDialog$: Observable<Erzeugungsanlage | never> = this.confirmDialogService.confirm$(
            'Mit Ihrer Änderung werden Planwerte (die auf diese Erzeugungsanlage verweisen) als <i>Ungültig</i> markiert. ' +
            '<br><br>' +
            'Möchten Sie diese Erzeugungsanlage trotzdem ändern?',
            'Planwerte werden als Ungültig markiert',
            'primary',
        ).pipe(switchMap((confirm) => iif(() => confirm, saveErzeugungsanlage$, EMPTY)));

        return checkErzeugungsanlageForPlanwertChanges$.pipe(
            switchMap(hasPlanwertChanges => iif(
                () => hasPlanwertChanges,
                openConfirmPlanwertChangesDialog$,
                saveErzeugungsanlage$,
            )),
        );
    }

    private countInvalidControls(): void {
        this.errorCount = 0;
        this.countRecursivelyInvalidControls(Object.values(this.form.controls));
        this.changeDetectorRef.markForCheck();
    }

    private countRecursivelyInvalidControls(abstractControls: AbstractControl[]): void {
        abstractControls.forEach(abstractControl => {
            if (abstractControl instanceof FormControl) {
                if (abstractControl.invalid) {
                    this.errorCount = this.errorCount + 1;
                }
            } else if (abstractControl instanceof FormGroup) {
                return this.countRecursivelyInvalidControls(Object.values((abstractControl as FormGroup).controls));
            } else if (abstractControl instanceof FormArray) {
                return this.countRecursivelyInvalidControls((abstractControl as FormArray).controls);
            }
        });
    }
}
